import { useState } from "react";
import { Reveal, SectionLabel } from "./Reveal";
import { Star, Plus, Minus } from "lucide-react";

const INTEGRATIONS = [
  "Twilio", "Google Calendar", "Calendly", "OpenAI", "ElevenLabs",
  "Anthropic", "WhatsApp", "Instagram", "Google Sheets", "Telnyx", "Messenger",
];

const TESTIMONIALS = [
  { name: "Marcus Chen", role: "Owner, Northridge Roofing", quote: "It answers every call now — even at 9pm on a Sunday. We booked 12 extra jobs last month from calls we used to miss.", stat: "12 extra jobs", letter: "M" },
  { name: "Jessica Williams", role: "Founder, Bright Smile Dental", quote: "Setup took five minutes. The AI sounds completely human and our no-shows dropped because it texts reminders automatically.", stat: "5 min setup", letter: "J" },
  { name: "David Park", role: "Owner, Park Auto Group", quote: "Customers think they're talking to a real receptionist. It remembers their last visit and books service appointments on its own.", stat: "100% coverage", letter: "D" },
  { name: "Amanda Torres", role: "Director, Torres Law", quote: "Every new client gets an instant response now. It screens them and only puts qualified consultations on my calendar.", stat: "3x more leads", letter: "A" },
  { name: "Priya Nair", role: "Owner, Lumen Fitness", quote: "It knows every member by name and follows up on trials automatically. Renewals are the best they've ever been.", stat: "32% more renewals", letter: "P" },
  { name: "Hannah Brooks", role: "Owner, BlueWave Med-Spa", quote: "Onboarded on a Tuesday, fully booked by Friday. It handles texts and calls without me lifting a finger.", stat: "47 bookings/wk", letter: "H" },
];

const FAQS = [
  { q: "Will it really sound like a real person?", a: "Yes. 5B AI uses the most natural-sounding voices available, responding in well under a second. Most callers can't tell they're speaking with an AI." },
  { q: "What can the AI actually do?", a: "It answers and makes phone calls, replies to texts, WhatsApp, Instagram, Facebook, and website chat, qualifies leads, answers common questions, and books appointments straight into your calendar — all day, every day." },
  { q: "Do I need to change my phone number?", a: "No. You can keep your existing number. We forward calls to your AI, or you can use a new dedicated number — whatever works best for you." },
  { q: "What if a customer needs a real person?", a: "You're always in control. The AI can transfer calls to you or your team, take a message, or book a callback based on rules you set." },
];

export const Social = () => {
  const [open, setOpen] = useState(0);

  return (
    <>
      {/* Integrations marquee */}
      <section data-testid="integrations-section" className="relative py-20 border-t border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center mb-10">
          <SectionLabel>Integrations</SectionLabel>
          <h2 className="font-heading mt-3 text-2xl md:text-3xl font-bold text-white">
            Works with the tools you already use.
          </h2>
        </div>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#050505] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#050505] to-transparent z-10" />
          <div className="flex w-max animate-marquee gap-4">
            {[...INTEGRATIONS, ...INTEGRATIONS].map((name, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-6 py-3 whitespace-nowrap"
              >
                <span className="h-2 w-2 rounded-full bg-brand" />
                <span className="font-medium text-white/70">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel>Testimonials</SectionLabel>
            <h2 className="font-heading mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white">
              Loved by local business owners.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 3) * 0.08}>
                <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#0c0c0c] p-7 transition-all duration-300 hover:border-white/25">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-brand text-brand" />
                    ))}
                  </div>
                  <p className="flex-1 text-[15px] leading-relaxed text-white/75">"{t.quote}"</p>
                  <div className="mt-6 flex items-center gap-3 pt-5 border-t border-white/10">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 font-bold text-brand">
                      {t.letter}
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-white">{t.name}</div>
                      <div className="text-xs text-white/45">{t.role}</div>
                    </div>
                    <span className="font-mono text-xs font-semibold text-brand">{t.stat}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal className="text-center">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="font-heading mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white">
              Questions? We've got answers.
            </h2>
          </Reveal>

          <div className="mt-12 space-y-3">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <Reveal key={f.q} delay={i * 0.04}>
                  <div
                    data-testid={`faq-item-${i}`}
                    className={`rounded-xl border bg-[#0c0c0c] transition-colors duration-300 ${isOpen ? "border-brand/40" : "border-white/10"}`}
                  >
                    <button
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                    >
                      <span className="font-heading text-base md:text-lg font-medium text-white">{f.q}</span>
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/5">
                        {isOpen ? <Minus className="h-4 w-4 text-brand" /> : <Plus className="h-4 w-4 text-white/60" />}
                      </span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <p className="px-6 pb-5 text-sm md:text-[15px] text-white/60 leading-relaxed">{f.a}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
